import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  Chip,
  Divider,
  Grid,
  Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { usePiers } from '../context/PiersContext';

const AssignmentAuditView = () => {
  const { flights, beltAssignments, configuration } = usePiers();

  // Create a map of flight assignments
  const getFlightAssignments = () => {
    const assignments = new Map();

    // Initialize all flights as unassigned
    flights.forEach(flight => {
      const key = `${flight.flightNum}-${flight.depTime}`;
      assignments.set(key, { flight, belt: null });
    });

    // Mark assigned flights with their belt numbers
    Object.entries(beltAssignments).forEach(([beltNum, beltFlights]) => {
      beltFlights.forEach(flight => {
        const key = `${flight.flightNum}-${flight.depTime}`;
        if (assignments.has(key)) {
          assignments.get(key).belt = beltNum;
        }
      });
    });

    return Array.from(assignments.values());
  };

  const flightAssignments = getFlightAssignments();
  const totalAssigned = flightAssignments.filter(a => a.belt !== null).length;
  const totalUnassigned = flightAssignments.filter(a => a.belt === null).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Summary Stats */}
      <Paper sx={{
        p: 2,
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#F9B612' }}>
                {flights.length}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Flights
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#4CAF50' }}>
                {totalAssigned}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Assigned
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: totalUnassigned > 0 ? '#FF5252' : '#666' }}>
                {totalUnassigned}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Unassigned
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {totalUnassigned > 0 && (
        <Alert severity="warning" sx={{
          bgcolor: 'rgba(255, 152, 0, 0.1)',
          color: '#FF9800',
          '& .MuiAlert-icon': { color: '#FF9800' }
        }}>
          {totalUnassigned} flight{totalUnassigned > 1 ? 's' : ''} not assigned to any belt
        </Alert>
      )}

      {/* Assignment List */}
      <Paper sx={{
        flexGrow: 1,
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <Box sx={{
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.08)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C0C0C0' }}>
                Flight Information
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#C0C0C0' }}>
                Belt Assignment
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Scrollable List */}
        <Box sx={{
          flexGrow: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(249, 182, 18, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(249, 182, 18, 0.5)',
            }
          }
        }}>
          <List sx={{ p: 0 }}>
            {flightAssignments
              .sort((a, b) => {
                // Sort unassigned to top, then by departure time
                if (a.belt === null && b.belt !== null) return -1;
                if (a.belt !== null && b.belt === null) return 1;
                return a.flight.depTime.localeCompare(b.flight.depTime);
              })
              .map(({ flight, belt }, index) => (
                <React.Fragment key={`${flight.flightNum}-${flight.depTime}-${index}`}>
                  <ListItem sx={{
                    py: 1.5,
                    px: 2,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.02)'
                    }
                  }}>
                    <Grid container alignItems="center">
                      {/* Left Side - Flight Info */}
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{
                            fontWeight: 600,
                            color: '#E2E2E2',
                            minWidth: '50px'
                          }}>
                            {flight.flightNum}
                          </Typography>
                          <Typography sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '0.875rem'
                          }}>
                            {flight.depTime}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {flight.destinations?.map((dest, idx) => (
                              <React.Fragment key={idx}>
                                {idx > 0 && <span style={{ color: '#666', fontSize: '0.7rem' }}>›</span>}
                                <Typography sx={{
                                  color: idx === 0 ? '#F9B612' : '#999',
                                  fontSize: idx === 0 ? '0.875rem' : '0.75rem',
                                  fontWeight: idx === 0 ? 500 : 400
                                }}>
                                  {dest}
                                </Typography>
                              </React.Fragment>
                            ))}
                          </Box>
                        </Box>
                      </Grid>

                      {/* Right Side - Belt Assignment */}
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {belt !== null ? (
                            <>
                              <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                              <Chip
                                label={configuration.beltNames?.[belt] || `Belt ${belt}`}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(76, 175, 80, 0.1)',
                                  color: '#4CAF50',
                                  border: '1px solid rgba(76, 175, 80, 0.3)',
                                  fontWeight: 600
                                }}
                              />
                            </>
                          ) : (
                            <>
                              <ErrorOutlineIcon sx={{ color: '#FF5252', fontSize: 20 }} />
                              <Typography sx={{
                                color: '#FF5252',
                                fontSize: '0.875rem',
                                fontStyle: 'italic'
                              }}>
                                Not Assigned
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </ListItem>
                  {index < flightAssignments.length - 1 && (
                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.05)' }} />
                  )}
                </React.Fragment>
              ))}
          </List>
        </Box>
      </Paper>
    </Box>
  );
};

export default AssignmentAuditView;